import { isNativeIntakeProfileVersion, nativeIntakeFields, type NativeIntakeProfileVersion } from '@ohf/core/shared';
import type { NativeSignerPayload } from './native-intake.js';

const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_BYTES = 300 * 1024;
const MAX_STRING = 4000;
const TOP_FIELDS = ['practiceName', 'signerName', 'email', 'signerTitle', 'signerCapacity'] as const;

export interface LocalDraft {
  payload: NativeSignerPayload;
  stage: number;
  baseFingerprint: string;
  savedAt: number;
}
export interface LocalDraftStore {
  readonly available: boolean;
  read(): LocalDraft | null;
  write(draft: Omit<LocalDraft, 'savedAt'>): boolean;
  clear(): boolean;
}
interface Options {
  formKey: string;
  profileVersion: NativeIntakeProfileVersion;
  context: { invitationCode?: string; linkCode?: string; resumeToken?: string; bundleToken?: string };
  storage?: Storage;
  now?: () => number;
  signal?: AbortSignal;
}
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const withinLimit = (value: string): boolean => value.length <= MAX_BYTES && new TextEncoder().encode(value).byteLength <= MAX_BYTES;

/** Explicitly copy answers only. Incomplete inputs are intentional; signing state is never a draft. */
function sanitize(value: unknown, expectedVersion?: NativeIntakeProfileVersion): NativeSignerPayload | null {
  if (!record(value) || !record(value.intakeProfile)) return null;
  const { schemaVersion, answers: rawAnswers } = value.intakeProfile;
  if (!isNativeIntakeProfileVersion(schemaVersion) || expectedVersion && schemaVersion !== expectedVersion || !record(rawAnswers)) return null;
  const payload: NativeSignerPayload = { signerName: '', email: '' };
  for (const key of TOP_FIELDS) {
    const answer = value[key] ?? '';
    if (typeof answer !== 'string' || answer.length > MAX_STRING) return null;
    payload[key] = answer;
  }
  const answers: Record<string, string | string[]> = {};
  for (const field of nativeIntakeFields(schemaVersion)) {
    const answer = rawAnswers[field.key];
    if (answer === undefined) continue;
    if (field.kind === 'multi') {
      if (!Array.isArray(answer) || answer.length > field.options.length || answer.some(item => typeof item !== 'string' || !field.options.includes(item))) return null;
      const selected = field.options.filter(option => answer.includes(option));
      if (selected.length) answers[field.key] = selected;
    } else {
      if (typeof answer !== 'string' || answer.length > MAX_STRING || answer && field.options.length && !field.options.includes(answer)) return null;
      if (answer) answers[field.key] = answer;
    }
  }
  payload.intakeProfile = { schemaVersion, answers };
  return payload;
}

/** Stable comparison of saved answers; ignores derived fields, credentials and signing state. */
export function draftFingerprint(payload: NativeSignerPayload): string {
  const clean = sanitize(payload);
  return clean ? JSON.stringify(clean) : '';
}

function validFingerprint(value: unknown, version: NativeIntakeProfileVersion): value is string {
  if (typeof value !== 'string' || !withinLimit(value)) return false;
  try {
    const clean = sanitize(JSON.parse(value), version);
    return !!clean && JSON.stringify(clean) === value;
  } catch { return false; }
}

/** Browser-only, scoped scratch answers. A failure disables persistence without blocking the form. */
export async function createLocalDraftStore(options: Options): Promise<LocalDraftStore> {
  let storage: Storage | undefined;
  let key = '';
  let available = false;
  let lastAnswers: { fingerprint: string; savedAt: number } | null = null;
  const now = options.now ?? Date.now;
  try {
    if (options.signal?.aborted) throw new Error('Draft initialization cancelled');
    const context = options.context;
    const scope = context.resumeToken ? ['resume', context.resumeToken]
      : context.bundleToken ? ['bundle', context.bundleToken]
      : context.linkCode ? ['link', context.linkCode]
      : context.invitationCode ? ['invitation', context.invitationCode] : ['direct'];
    const bytes = new TextEncoder().encode(JSON.stringify([options.formKey, options.profileVersion, ...scope]));
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    if (options.signal?.aborted) throw new Error('Draft initialization cancelled');
    storage = options.storage ?? globalThis.localStorage;
    const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    key = `ohf:native-draft:1:${encodeURIComponent(options.formKey)}:${options.profileVersion}:${hash}`;
    storage.getItem(key);
    available = true;
  } catch { /* Storage or WebCrypto can be unavailable in restricted browsers. */ }

  const clear = (): boolean => {
    // A quota/read failure can disable saving while removal still works.
    if (!storage || !key) return false;
    try { storage.removeItem(key); lastAnswers = null; return true; }
    catch { available = false; return false; }
  };
  const read = (): LocalDraft | null => {
    if (!available || !storage) return null;
    let raw: string | null;
    try { raw = storage.getItem(key); }
    catch { available = false; return null; }
    if (raw === null) return null;
    try {
      if (!withinLimit(raw)) throw new Error('Oversized draft');
      const entry: unknown = JSON.parse(raw);
      if (!record(entry) || entry.version !== 1 || !Number.isInteger(entry.savedAt) || typeof entry.savedAt !== 'number'
        || entry.savedAt < 0 || entry.savedAt > now()
        || typeof entry.stage !== 'number' || !Number.isInteger(entry.stage) || entry.stage < 0 || entry.stage > 4
        || !validFingerprint(entry.baseFingerprint, options.profileVersion)) throw new Error('Invalid draft');
      const payload = sanitize(entry.payload, options.profileVersion);
      if (!payload || JSON.stringify(payload) !== JSON.stringify(entry.payload)) throw new Error('Invalid answers');
      const remembered = { fingerprint: draftFingerprint(payload), savedAt: entry.savedAt };
      if (now() - entry.savedAt >= RETENTION_MS) {
        clear();
        // The open page may still hold these answers. Navigation must not renew them.
        lastAnswers = remembered;
        return null;
      }
      lastAnswers = remembered;
      return { payload, stage: entry.stage, baseFingerprint: entry.baseFingerprint, savedAt: entry.savedAt };
    } catch { clear(); return null; }
  };
  return {
    get available() { return available; },
    read,
    clear,
    write(draft) {
      if (!available || !storage) return false;
      try {
        const payload = sanitize(draft.payload, options.profileVersion);
        if (!payload || !validFingerprint(draft.baseFingerprint, options.profileVersion) || !Number.isFinite(draft.stage)) return false;
        read();
        if (!available) return false;
        // Moving between sections or repeating a save does not extend answer retention.
        const fingerprint = draftFingerprint(payload);
        const savedAt = lastAnswers?.fingerprint === fingerprint ? lastAnswers.savedAt : now();
        if (!Number.isInteger(savedAt) || savedAt < 0) return false;
        if (now() - savedAt >= RETENTION_MS) return false;
        const json = JSON.stringify({ version: 1, payload, stage: Math.max(0, Math.min(4, Math.floor(draft.stage))), baseFingerprint: draft.baseFingerprint, savedAt });
        if (!withinLimit(json)) return false;
        storage.setItem(key, json);
        lastAnswers = { fingerprint, savedAt };
        return true;
      } catch { available = false; return false; }
    },
  };
}
